'use client';

import React from 'react';
import { useAccordionSummary } from '../../context/AccordionProvider';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import type { AccordionSize } from '../../Accordion'; // Correct import path

export interface AccordionSummaryProps {
  children: React.ReactNode;
  index: number;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  className?: string;
  sx?: React.CSSProperties;
}

const AccordionSummary = ({
  children,
  index,
  startDecorator,
  endDecorator,
  className,
  sx,
}: AccordionSummaryProps): React.ReactElement => {
  const { isOpen, onToggle, size } = useAccordionSummary(index);

  // Define size-based styles
  const sizeStyles: Record<AccordionSize, string> = {
    sm: 'p-2 text-sm gap-1.5', // Smaller padding, text, gap
    md: 'p-3 text-base gap-2', // Default padding, text, gap
    lg: 'p-4 text-lg gap-2.5', // Larger padding, text, gap
  };

  return (
    <div
      role="button"
      className={twMerge(
        'w-full flex items-center text-left font-medium',
        'hover:bg-neutral-100',
        'transition-colors cursor-pointer',
        sizeStyles[size], // Apply size styles
        className // User classes override defaults
      )}
      style={sx}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`accordion-details-${index}`}
      id={`accordion-item-${index}`}
    >
      {startDecorator && <div className="flex-shrink-0">{startDecorator}</div>}
      <div className="flex-grow">{children}</div>
      <div
        className={twMerge(
          'flex-shrink-0 transform transition-transform duration-300 ease-in-out',
          // Apply text size to the icon container as well to ensure relative scaling
          sizeStyles[size].split(' ').find((cls) => cls.startsWith('text-')),
          isOpen ? 'rotate-0' : '-rotate-90'
        )}
      >
        {endDecorator ? endDecorator : <Icon icon={faChevronDown} />}
      </div>
    </div>
  );
};

export default AccordionSummary;
